import { setSession } from '../session.js';
import { showToast } from './Toast.js';
import type { Business } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export function renderBusinessRegister(container: HTMLElement, navigate: NavigateFn): void {
  container.innerHTML = `
    <div class="auth-wrap">
      <div class="auth-card">
        <div class="auth-logo">PREUVU</div>
        <div class="auth-tagline">Digital Notary Platform</div>

        <div class="auth-tabs">
          <button class="auth-tab active" data-tab="login">
            <i class="bi bi-box-arrow-in-right"></i> Sign In
          </button>
          <button class="auth-tab" data-tab="register">
            <i class="bi bi-building-add"></i> Register
          </button>
          <button class="auth-tab" data-tab="admin">
            <i class="bi bi-shield-lock"></i> Admin
          </button>
        </div>

        <!-- LOGIN TAB (first) -->
        <div id="tab-login" class="tab-panel">
          <p style="font-size:13px;color:var(--text-muted);margin-bottom:16px">
            Enter your Business ID to access your account.
          </p>
          <div class="form-row">
            <label>Business ID</label>
            <input type="text" id="login-biz-id" placeholder="Paste your Business ID…" autocomplete="off" />
          </div>
          <div class="form-row">
            <label>State</label>
            <select id="login-state">
              <option value="WA">Washington (WA)</option>
              <option value="CA">California (CA)</option>
              <option value="TX">Texas (TX)</option>
              <option value="FL">Florida (FL)</option>
              <option value="NY">New York (NY)</option>
              <option value="OR">Oregon (OR)</option>
              <option value="ID">Idaho (ID)</option>
              <option value="MT">Montana (MT)</option>
              <option value="other">Other</option>
            </select>
          </div>
          <button class="btn btn-primary btn-lg" style="width:100%" id="login-btn">
            <i class="bi bi-box-arrow-in-right"></i> Access Dashboard
          </button>
          <div style="margin-top:20px;padding-top:16px;border-top:1px solid var(--border)">
            <p style="font-size:11px;color:var(--text-muted);margin-bottom:10px">Quick demo access:</p>
            <div style="display:flex;gap:8px;flex-wrap:wrap">
              <button class="btn btn-ghost btn-sm" id="demo-construction">
                <i class="bi bi-building"></i> Construction Demo
              </button>
              <button class="btn btn-ghost btn-sm" id="demo-restaurant">
                <i class="bi bi-cup-hot"></i> Restaurant Demo
              </button>
              <button class="btn btn-ghost btn-sm" id="demo-general">
                <i class="bi bi-briefcase"></i> General Demo
              </button>
            </div>
          </div>
        </div>

        <!-- REGISTER TAB -->
        <div id="tab-register" class="tab-panel" style="display:none">
          <form id="register-form">
            <div class="form-row">
              <label>Business Name *</label>
              <input type="text" id="biz-name" placeholder="Apex Construction LLC" required />
            </div>
            <div class="form-row-2">
              <div>
                <label>Industry *</label>
                <select id="biz-industry" required>
                  <option value="">Select industry…</option>
                  <option value="construction">🏗 Construction</option>
                  <option value="restaurant">🍽 Restaurant / Food Service</option>
                  <option value="general">💼 General Business</option>
                </select>
              </div>
              <div>
                <label>State *</label>
                <select id="biz-state" required>
                  <option value="WA">Washington (WA)</option>
                  <option value="CA">California (CA)</option>
                  <option value="TX">Texas (TX)</option>
                  <option value="FL">Florida (FL)</option>
                  <option value="NY">New York (NY)</option>
                  <option value="OR">Oregon (OR)</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div class="form-row-2">
              <div>
                <label>Business Email *</label>
                <input type="email" id="biz-email" placeholder="info@yourbusiness.com" required />
              </div>
              <div>
                <label>Phone *</label>
                <input type="tel" id="biz-phone" placeholder="(555) 000-0000" required />
              </div>
            </div>
            <div class="form-row">
              <label>City, State *</label>
              <input type="text" id="biz-address" placeholder="Seattle, WA" required />
            </div>
            <div class="form-row">
              <label>Tell us about your business</label>
              <textarea id="biz-desc" placeholder="Brief description of what you do and why you need Preuve AI…" style="min-height:70px"></textarea>
            </div>
            <button type="submit" class="btn btn-gold btn-lg" style="width:100%" id="register-btn">
              <i class="bi bi-send"></i> Submit for Approval
            </button>
            <p style="font-size:11px;color:var(--text-muted);text-align:center;margin-top:12px">
              An admin will review your application within 24 hours.
            </p>
          </form>
        </div>

        <!-- ADMIN TAB -->
        <div id="tab-admin" class="tab-panel" style="display:none">
          <p style="font-size:13px;color:var(--text-muted);margin-bottom:16px">
            Enter admin PIN to access the control panel.
          </p>
          <div class="form-row">
            <label>Admin PIN</label>
            <input type="password" id="admin-pin" placeholder="••••" maxlength="8"
              style="letter-spacing:4px;font-size:20px;text-align:center" />
          </div>
          <button class="btn btn-primary btn-lg" style="width:100%" id="admin-login-btn">
            <i class="bi bi-shield-check"></i> Enter Admin Panel
          </button>
          <p style="font-size:11px;color:var(--text-muted);text-align:center;margin-top:10px">Default PIN: 1234</p>
        </div>
      </div>
    </div>

    <!-- Paused Account Modal -->
    <div id="paused-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:2000;align-items:center;justify-content:center">
      <div class="card" style="width:420px;text-align:center;padding:32px">
        <div style="font-size:48px;margin-bottom:16px">⚠️</div>
        <h2 style="font-size:18px;font-weight:800;margin-bottom:8px;color:var(--warning)">Account Paused</h2>
        <p style="font-size:13px;color:var(--text-muted);margin-bottom:8px" id="paused-reason">
          Your account has been temporarily suspended by an administrator.
        </p>
        <p style="font-size:13px;color:var(--text-secondary);margin-bottom:24px">
          To restore access, please contact a Preuve AI Administrator.
        </p>
        <a href="mailto:admin@preuve.ai" class="btn btn-gold btn-lg" style="width:100%;text-decoration:none">
          <i class="bi bi-envelope"></i> Contact Administrator
        </a>
        <button class="btn btn-ghost" style="width:100%;margin-top:8px" id="close-paused-modal">Go Back</button>
      </div>
    </div>
  `;

  // Tab switching
  container.querySelectorAll('.auth-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      container.querySelectorAll('.auth-tab').forEach((t) => t.classList.remove('active'));
      container.querySelectorAll('.tab-panel').forEach((p) => ((p as HTMLElement).style.display = 'none'));
      tab.classList.add('active');
      const target = tab.getAttribute('data-tab')!;
      document.getElementById(`tab-${target}`)!.style.display = 'block';
    });
  });

  // Register
  document.getElementById('register-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = document.getElementById('register-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span> Submitting…';
    try {
      const payload = {
        name: (document.getElementById('biz-name') as HTMLInputElement).value,
        industry: (document.getElementById('biz-industry') as HTMLSelectElement).value,
        state: (document.getElementById('biz-state') as HTMLSelectElement).value,
        email: (document.getElementById('biz-email') as HTMLInputElement).value,
        phone: (document.getElementById('biz-phone') as HTMLInputElement).value,
        address: (document.getElementById('biz-address') as HTMLInputElement).value,
        description: (document.getElementById('biz-desc') as HTMLTextAreaElement).value,
        ownerId: crypto.randomUUID(),
      };
      const api = getAPI();
      const biz = await api.registerBusiness(payload) as Business;
      navigate('pending', { businessId: biz.id, businessName: biz.name });
    } catch (err) {
      showToast((err as Error).message, 'error');
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-send"></i> Submit for Approval';
    }
  });

  // Login with business ID
  document.getElementById('login-btn')!.addEventListener('click', async () => {
    const bizId = (document.getElementById('login-biz-id') as HTMLInputElement).value.trim();
    const state = (document.getElementById('login-state') as HTMLSelectElement).value;
    if (!bizId) { showToast('Enter a Business ID', 'error'); return; }
    await attemptLogin(bizId, state, navigate);
  });

  // Demo buttons
  document.getElementById('demo-construction')!.addEventListener('click', () => createDemo('construction', navigate));
  document.getElementById('demo-restaurant')!.addEventListener('click', () => createDemo('restaurant', navigate));
  document.getElementById('demo-general')!.addEventListener('click', () => createDemo('general', navigate));

  // Admin login
  document.getElementById('admin-login-btn')!.addEventListener('click', async () => {
    const pin = (document.getElementById('admin-pin') as HTMLInputElement).value;
    try {
      const api = getAPI();
      const ok = await api.verifyAdminPin(pin);
      if (!ok) { showToast('Incorrect PIN', 'error'); return; }
      setSession({ businessId: 'admin', businessName: 'Admin', industry: 'general', isAdmin: true });
      navigate('admin');
    } catch {
      if (pin === '1234') {
        setSession({ businessId: 'admin', businessName: 'Admin', industry: 'general', isAdmin: true });
        navigate('admin');
      } else {
        showToast('Incorrect PIN', 'error');
      }
    }
  });

  // Paused modal close
  document.getElementById('close-paused-modal')!.addEventListener('click', () => {
    document.getElementById('paused-modal')!.style.display = 'none';
  });
}

async function attemptLogin(bizId: string, state: string, navigate: NavigateFn): Promise<void> {
  try {
    const api = getAPI();
    const biz = await api.getBusiness(bizId) as Business | null;
    if (!biz) { showToast('Business ID not found', 'error'); return; }
    if (biz.status === 'pending') { navigate('pending', { businessId: biz.id, businessName: biz.name }); return; }
    if (biz.status === 'rejected') { showToast(`Application rejected: ${biz.rejectionReason ?? 'Contact support'}`, 'error'); return; }
    if (biz.isPaused) {
      // Route to the full hold resolution page instead of a basic modal
      navigate('account-hold', { businessId: biz.id, businessName: biz.name });
      return;
    }
    setSession({ businessId: biz.id, businessName: biz.name, industry: biz.industry, isAdmin: false, state: biz.state ?? state });
    navigate('dashboard');
  } catch {
    showToast('Could not connect. Try demo mode.', 'error');
  }
}

async function createDemo(industry: string, navigate: NavigateFn): Promise<void> {
  const names: Record<string, string> = {
    construction: 'Apex Construction LLC (Demo)',
    restaurant: 'The Golden Fork (Demo)',
    general: 'Summit Solutions (Demo)',
  };
  try {
    const api = getAPI();
    const biz = await api.registerBusiness({
      name: names[industry]!,
      industry: industry as 'construction' | 'restaurant' | 'general',
      state: 'WA',
      email: 'demo@preuve.ai',
      phone: '(555) 000-0000',
      address: 'Seattle, WA',
      description: 'Demo account',
      ownerId: crypto.randomUUID(),
    }) as Business;
    await api.approveBusiness(biz.id);
    setSession({ businessId: biz.id, businessName: biz.name, industry: biz.industry, isAdmin: false, state: 'WA' });
    navigate('dashboard');
  } catch {
    setSession({ businessId: `demo-${industry}`, businessName: names[industry]!, industry, isAdmin: false, state: 'WA' });
    navigate('dashboard');
  }
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
